import {
  Table,
  Button,
  Spin,
  Popover,
  Typography,
  Switch,
  Modal,
  Input,
  Upload,
  Form,
  Select,
  Space,
  Tooltip,
  Badge,
  Drawer,
  Dropdown,
  Menu,
  message,
  Empty
} from "antd";
import { PictureOutlined, EditOutlined, UploadOutlined, HolderOutlined, SettingOutlined, DownOutlined } from "@ant-design/icons";
import FallbackImage from "../../../components/FallbackImage";
import { useState, useEffect, useMemo } from "react";
import { getColumnSearchProps } from "../../../utils/tables";
import Header from "../../../components/HeaderList";
import styles from "../styles/CategoriesUI.module.css";

const { Text } = Typography;

const FALLBACK_PHOTO = <PictureOutlined style={{ fontSize: '20px', color: '#ccc' }} />;
const FALLBACK_BANNER = <div className={styles.tableBannerPlaceholder}><PictureOutlined /></div>;
const FALLBACK_ICON_LARGE = <PictureOutlined style={{ fontSize: '24px', color: '#ccc' }} />;
const FALLBACK_ICON_PLACEHOLDER = <PictureOutlined className={styles.tableIconPlaceholder} />;

const CategoriesUI = (props) => {
  const {
    loading,
    categories,
    onImageUpload,
    onToggleActive,
    onResetOverride,
    onResetAllOverrides,
    onUpdateName,
    channels,
    selectedChannelId,
    onChannelChange,
    handleDragDropId,
    customizableChannels,
    loadingCustomizable,
    savingCustomizable,
    fetchCustomizableChannels,
    onSaveCustomizableChannels,
    fetchCategoryProducts
  } = props;

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingName, setSavingName] = useState(false);

  // States for image upload modal
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadModalCategory, setUploadModalCategory] = useState(null);
  const [uploadModalType, setUploadModalType] = useState(null); // 'banner' | 'icon' | 'icon_app'
  const [uploadModalFile, setUploadModalFile] = useState(null);
  const [uploadModalPreview, setUploadModalPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeUploadModal = () => {
    setUploadModalVisible(false);
    setUploadModalCategory(null);
    setUploadModalType(null);
    setUploadModalFile(null);
    setUploadModalPreview(null);
    setIsSubmitting(false);
  };

  const handleUploadConfirm = () => {
    if (!uploadModalCategory || !uploadModalFile || !uploadModalType) return;
    setIsSubmitting(true);
    onImageUpload(uploadModalCategory.id, uploadModalFile, uploadModalType);
  };

  // States for category products drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCategoryForProducts, setSelectedCategoryForProducts] = useState(null);
  const [drawerProducts, setDrawerProducts] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const handleOpenProductsDrawer = async (categoryRecord) => {
    setSelectedCategoryForProducts(categoryRecord);
    setDrawerVisible(true);
    setDrawerProducts([]);
    setDrawerLoading(true);
    try {
      const response = await fetchCategoryProducts(categoryRecord.id);
      if (response && response.data && response.data.status === "success") {
        setDrawerProducts(response.data.products || []);
      } else {
        message.error("Error al cargar los productos de la categoría");
      }
    } catch (err) {
      console.error("Error fetching category products:", err);
      message.error("Error al cargar los productos de la categoría");
    } finally {
      setDrawerLoading(false);
    }
  };

  const drawerColumns = useMemo(() => [
    {
      title: "Foto",
      dataIndex: "photo",
      align: "center",
      width: 70,
      render: (photo) => {
        if (!photo) {
          return <PictureOutlined style={{ fontSize: '18px', color: '#ccc' }} />;
        }
        const content = (
          <div style={{ padding: '4px' }}>
            <FallbackImage src={photo} alt="Producto" style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }} fallback={FALLBACK_PHOTO} />
          </div>
        );
        return (
          <Popover content={content} trigger="hover" placement="right">
            <PictureOutlined style={{ fontSize: '18px', color: '#1890ff', cursor: 'pointer' }} />
          </Popover>
        );
      }
    },
    {
      title: "SKU",
      dataIndex: "sku",
      width: 120,
    },
    {
      title: "Nombre",
      dataIndex: "name",
      render: (val) => <Text strong style={{ textTransform: "uppercase" }}>{val || "-"}</Text>
    },
    {
      title: "Precio",
      dataIndex: "price",
      align: "center",
      width: 100,
      render: (val) => `$${(val || 0).toFixed(2)}`
    },
    {
      title: "Canales (Disponibilidad)",
      align: "center",
      width: 160,
      render: (_, productRecord) => (
        <Space size={4}>
          {productRecord?.channels?.map(p => (
            <Tooltip key={p.name} title={`${p.name.toUpperCase()}: ${p.active ? 'Activo' : 'Inactivo'}`}>
              <Badge color={p.active ? 'green' : 'red'} />
            </Tooltip>
          ))}
        </Space>
      )
    }
  ], []);

  // States for customizable channels modal config
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [tempCustomizableChannels, setTempCustomizableChannels] = useState([]);

  useEffect(() => {
    if (customizableChannels) {
      setTempCustomizableChannels(customizableChannels);
    }
  }, [customizableChannels]);

  const handleOpenConfigModal = () => {
    setConfigModalVisible(true);
    fetchCustomizableChannels();
  };

  const handleToggleChannelCustomizable = (channelId, checked) => {
    setTempCustomizableChannels(prev =>
      prev.map(ch => ch.id === channelId ? { ...ch, isCustomizable: checked } : ch)
    );
  };

  const handleSaveConfig = async () => {
    const activeIds = tempCustomizableChannels
      .filter(ch => ch.isCustomizable)
      .map(ch => ch.id);
    const success = await onSaveCustomizableChannels(activeIds);
    if (success) {
      setConfigModalVisible(false);
    }
  };
  const [draggedRowId, setDraggedRowId] = useState(null);
  const [draggedOverRowId, setDraggedOverRowId] = useState(null);

  const openEditModal = (record) => {
    setEditingCategory(record);
    setEditName(record.name || "");
    setEditModalVisible(true);
  };

  const closeEditModal = () => {
    setEditModalVisible(false);
    setEditingCategory(null);
    setEditName("");
  };

  const handleSaveName = async () => {
    if (!editingCategory) return;
    if (editName.trim() === "") return;
    setSavingName(true);
    await onUpdateName(editingCategory.id, editName.trim());
    setSavingName(false);
    closeEditModal();
  };

  const columns = useMemo(() => {
    const cols = [
      {
        title: "Orden",
        align: "center",
        width: 80,
        render: () => {
          return (
            <span
              className={styles.dragger}
              style={{
                cursor: "grab",
                opacity: 1
              }}
            >
              <HolderOutlined />
            </span>
          );
        },
      },
      {
        title: "ID",
        dataIndex: "id",
        width: 80,
        align: "center",
        sorter: (a, b) => a.id - b.id,
      },
      {
        title: "Nombre",
        dataIndex: "name",
        ...getColumnSearchProps("name"),
        render: (val) => <Text strong style={{ textTransform: "uppercase" }}>{val || "-"}</Text>,
      },
      {
        title: "Productos",
        dataIndex: "productCount",
        align: "center",
        width: 110,
        sorter: (a, b) => (a.productCount || 0) - (b.productCount || 0),
        render: (val, record) => (
          <Badge
            count={val}
            showZero
            overflowCount={999}
            style={{ backgroundColor: '#108ee9' }}
          >
            <Button
              size="small"
              onClick={() => handleOpenProductsDrawer(record)}
            >
              Ver
            </Button>
          </Badge>
        ),
      },
      {
        title: "Banner",
        dataIndex: "image_banner_url",
        align: "center",
        width: 100,
        render: (photo, record) => {
          const uploadTrigger = (
            <Upload
              beforeUpload={(file) => {
                onImageUpload(record.id, file, 'banner');
                return false;
              }}
              showUploadList={false}
              accept="image/*"
            >
              {photo ? (
                <FallbackImage
                  src={photo}
                  alt={`Banner de ${record.name}`}
                  className={styles.tableBannerPreview}
                  fallback={FALLBACK_BANNER}
                />
              ) : (
                <div className={styles.tableBannerPlaceholder}>
                  <PictureOutlined />
                </div>
              )}
            </Upload>
          );

          if (!photo) {
            return uploadTrigger;
          }

          const content = (
            <div className={styles["image-container"]}>
              <img
                src={photo}
                alt="Banner de Categoría"
              />
              <div>ID: {record.id}</div>
            </div>
          );

          return (
            <Popover content={content} trigger="hover" placement="right">
              {uploadTrigger}
            </Popover>
          );
        }
      },
      {
        title: "Iconos",
        align: "center",
        width: 110,
        render: (_, record) => {
          const webPhoto = record.image_icon_url;
          const appPhoto = record.image_icon_app_url;
          const webTooltip = "Haga clic para subir nuevo Icono Web";
          const appTooltip = "Haga clic para subir nuevo Icono App";

          const content = (
            <div style={{ display: 'flex', gap: '16px', padding: '8px', alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ background: '#f0f0f0', padding: '8px', borderRadius: '4px', display: 'inline-block' }}>
                  {webPhoto ? (
                    <FallbackImage src={webPhoto} alt="Web Icon" style={{ width: '48px', height: '48px', objectFit: 'contain' }} fallback={FALLBACK_ICON_LARGE} />
                  ) : (
                    <PictureOutlined style={{ fontSize: '24px', color: '#ccc' }} />
                  )}
                </div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', fontWeight: 'bold' }}>Web</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ background: '#f0f0f0', padding: '8px', borderRadius: '4px', display: 'inline-block' }}>
                  {appPhoto ? (
                    <FallbackImage src={appPhoto} alt="App Icon" style={{ width: '48px', height: '48px', objectFit: 'contain' }} fallback={FALLBACK_ICON_LARGE} />
                  ) : (
                    <PictureOutlined style={{ fontSize: '24px', color: '#ccc' }} />
                  )}
                </div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', fontWeight: 'bold' }}>App</div>
              </div>
            </div>
          );

          return (
            <Popover content={content} trigger="hover" placement="right" title={`Iconos de: ${record.name.toUpperCase()}`}>
              <div className={styles.tableIconsContainer}>
                <Tooltip title={webTooltip}>
                  <Upload
                    beforeUpload={(file) => {
                      onImageUpload(record.id, file, 'icon');
                      return false;
                    }}
                    showUploadList={false}
                    accept="image/*,image/svg+xml"
                  >
                    <div className={styles.tableIconBox}>
                      {webPhoto ? (
                        <FallbackImage src={webPhoto} alt="W" className={styles.tableIconImage} fallback={FALLBACK_ICON_PLACEHOLDER} />
                      ) : (
                        <PictureOutlined className={styles.tableIconPlaceholder} />
                      )}
                    </div>
                  </Upload>
                </Tooltip>
                <Tooltip title={appTooltip}>
                  <Upload
                    beforeUpload={(file) => {
                      onImageUpload(record.id, file, 'icon_app');
                      return false;
                    }}
                    showUploadList={false}
                    accept="image/png"
                  >
                    <div className={styles.tableIconBox}>
                      {appPhoto ? (
                        <FallbackImage src={appPhoto} alt="A" className={styles.tableIconImage} fallback={FALLBACK_ICON_PLACEHOLDER} />
                      ) : (
                        <PictureOutlined className={styles.tableIconPlaceholder} />
                      )}
                    </div>
                  </Upload>
                </Tooltip>
              </div>
            </Popover>
          );
        }
      },
      {
        title: "Disponibilidad",
        align: "center",
        width: 140,
        render: (_, record) => (
          <Space size={4}>
            {record?.channels?.map(p => (
              <Tooltip key={p.name} title={`${p.name.toUpperCase()}: ${p.active ? 'Activo' : 'Inactivo'}`}>
                <Badge color={p.active ? 'green' : 'red'} />
              </Tooltip>
            ))}
          </Space>
        ),
      }
    ];

    // Insertar columna "Personalizado" si hay un canal seleccionado
    if (selectedChannelId) {
      cols.push({
        title: "Personalizado",
        align: "center",
        width: 120,
        render: (_, record) => {
          const chRule = record.channels?.find(c => c.channel_id === selectedChannelId);
          const isOverride = chRule ? chRule.isOverride : false;
          const activeVal = chRule ? chRule.active : record.active;

          return (
            <Switch
              checked={isOverride}
              onChange={(checked) => {
                if (checked) {
                  onToggleActive(record.id, activeVal === 1);
                } else {
                  Modal.confirm({
                    title: "¿Desactivar personalización?",
                    content: "Esta categoría volverá a heredar el estado activo y orden global del canal. Todos los cambios locales de este canal se perderán.",
                    okText: "Sí, restablecer",
                    cancelText: "Cancelar",
                    onOk: () => {
                      onResetOverride(record.id);
                    }
                  });
                }
              }}
            />
          );
        }
      });
    }

    cols.push({
      title: "Activo",
      dataIndex: "active",
      align: "center",
      width: 120,
      filters: [
        { text: "Activo", value: 1 },
        { text: "Inactivo", value: 0 },
      ],
      filterMultiple: false,
      onFilter: (value, record) => {
        let activeVal = record.active;
        if (selectedChannelId) {
          const chRule = record.channels?.find(c => c.channel_id === selectedChannelId);
          activeVal = chRule ? chRule.active : record.active;
        }
        return activeVal === value;
      },
      render: (val, record) => {
        let activeVal = val;
        let isOverride = false;
        if (selectedChannelId) {
          const chRule = record.channels?.find(c => c.channel_id === selectedChannelId);
          activeVal = chRule ? chRule.active : val;
          isOverride = chRule ? chRule.isOverride : false;
        }
        const disabled = selectedChannelId && !isOverride;
        return (
          <Switch
            checked={activeVal === 1}
            onChange={(checked) => onToggleActive(record.id, checked)}
            disabled={disabled}
          />
        );
      },
    });

    cols.push({
      title: "Acciones",
      align: "center",
      width: 140,
      render: (_, record) => {
        const actionsMenu = (
          <Menu>
            <Menu.Item key="edit-name" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
              Editar Nombre
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="upload-banner" icon={<UploadOutlined />}>
              <Upload
                beforeUpload={(file) => {
                  onImageUpload(record.id, file, 'banner');
                  return false;
                }}
                showUploadList={false}
                accept="image/*"
              >
                <span style={{ cursor: 'pointer', display: 'block', width: '100%' }}>Subir Banner</span>
              </Upload>
            </Menu.Item>
            <Menu.Item key="upload-icon" icon={<UploadOutlined />}>
              <Upload
                beforeUpload={(file) => {
                  onImageUpload(record.id, file, 'icon');
                  return false;
                }}
                showUploadList={false}
                accept="image/*,image/svg+xml"
              >
                <span style={{ cursor: 'pointer', display: 'block', width: '100%' }}>Subir Icono Web</span>
              </Upload>
            </Menu.Item>
            <Menu.Item key="upload-icon-app" icon={<UploadOutlined />}>
              <Upload
                beforeUpload={(file) => {
                  onImageUpload(record.id, file, 'icon_app');
                  return false;
                }}
                showUploadList={false}
                accept="image/png"
              >
                <span style={{ cursor: 'pointer', display: 'block', width: '100%' }}>Subir Icono App</span>
              </Upload>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={actionsMenu} trigger={['click']}>
            <Button className={styles.actionButton} icon={<SettingOutlined />}>
              Acciones <DownOutlined style={{ fontSize: '10px' }} />
            </Button>
          </Dropdown>
        );
      }
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, customizableChannels, categories, draggedRowId, draggedOverRowId, selectedChannelId]);

  return (
    <div className={styles.view}>
      <Header title="MANEJO DE CATEGORÍAS" />
      {loading ? (
        <div className={styles.loader}>
          <Spin size="large" />
        </div>
      ) : (
        <div className={styles.tableContainer}>

          <div className={styles.toolbar}>
            <Text strong className={styles.channelTitle}>
              {selectedChannelId ? `Canal: ${channels.find(c => c.id === selectedChannelId)?.name.toUpperCase()}` : "Canal: Global"}
            </Text>
            <Space wrap>
              {selectedChannelId && (
                <Button
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: "¿Restablecer todos los overrides?",
                      content: "Esta acción eliminará todas las personalizaciones (estado activo y orden) de este canal, volviendo a heredar la configuración global. Esta acción no se puede deshacer.",
                      okText: "Restablecer todo",
                      cancelText: "Cancelar",
                      onOk: () => {
                        onResetAllOverrides();
                      }
                    });
                  }}
                >
                  Restablecer Canal
                </Button>
              )}
              <Text type="secondary">Configurar Canal:</Text>
              <Select
                className={styles.channelSelect}
                placeholder="Seleccione un canal"
                value={selectedChannelId}
                onChange={onChannelChange}
              >
                <Select.Option value={null}>Global</Select.Option>
                {(channels || []).map(ch => (
                  <Select.Option key={ch.id} value={ch.id}>
                    {ch.name.toUpperCase()}
                  </Select.Option>
                ))}
              </Select>
              <Tooltip title="Habilitar/Deshabilitar Canales Personalizables">
                <Button
                  icon={<SettingOutlined />}
                  onClick={handleOpenConfigModal}
                />
              </Tooltip>
            </Space>
          </div>

          {(!categories || categories.length === 0) ? (
            <Empty description="No hay categorías para mostrar" style={{ margin: '40px 0' }} />
          ) : (
            <Table
              scroll={{ x: 1000 }}
              columns={columns}
              dataSource={categories}
              rowKey="id"
              pagination={false}
              bordered
              onRow={(record) => {
                const isDragging = record.id === draggedRowId;
                const isDragOver = record.id === draggedOverRowId;

                let rowClass = "";
                if (isDragging) rowClass = styles.rowDragging;
                else if (isDragOver) rowClass = styles.rowDragOver;

                return {
                  draggable: true,
                  className: rowClass,
                  style: { cursor: "grab" },
                  onDragStart: (event) => {
                    setDraggedRowId(record.id);
                    event.dataTransfer.setData("draggedId", record.id);
                    event.dataTransfer.effectAllowed = "move";
                  },
                  onDragOver: (event) => {
                    event.preventDefault();
                    if (draggedOverRowId !== record.id) {
                      setDraggedOverRowId(record.id);
                    }
                  },
                  onDragLeave: () => {
                    if (draggedOverRowId === record.id) {
                      setDraggedOverRowId(null);
                    }
                  },
                  onDragEnd: () => {
                    setDraggedRowId(null);
                    setDraggedOverRowId(null);
                  },
                  onDrop: (event) => {
                    const draggedId = Number(event.dataTransfer.getData("draggedId"));
                    const targetId = Number(record.id);
                    setDraggedRowId(null);
                    setDraggedOverRowId(null);
                    if (draggedId && targetId && draggedId !== targetId) {
                      handleDragDropId(draggedId, targetId);
                    }
                  },
                };
              }}
            />
          )}
        </div>
      )}

      {/* Modal de edición */}
      <Modal
        title={editingCategory ? `Editar Nombre - Categoría #${editingCategory.id}` : "Editar Categoría"}
        visible={editModalVisible}
        onCancel={closeEditModal}
        footer={null}
        destroyOnClose
        width={420}
      >
        {editingCategory && (
          <div>
            <Form layout="vertical">
              <Form.Item label="Nombre de la categoría" style={{ marginBottom: 16 }}>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nombre de la categoría"
                  onPressEnter={handleSaveName}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={closeEditModal}>
                    Cancelar
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSaveName}
                    loading={savingName}
                    disabled={editName.trim() === "" || editName.trim() === editingCategory.name}
                  >
                    Guardar
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Modal para subir imagen */}
      <Modal
        title={
          uploadModalCategory && uploadModalType ? (
            <span>
              <UploadOutlined /> Subir {
                uploadModalType === "banner" ? "Banner" :
                uploadModalType === "icon" ? "Icono Web" : "Icono App"
              } - {uploadModalCategory.name.toUpperCase()}
            </span>
          ) : "Subir Imagen"
        }
        visible={uploadModalVisible}
        onCancel={isSubmitting ? null : closeUploadModal}
        footer={[
          <Button key="cancel" onClick={closeUploadModal} disabled={isSubmitting}>
            Cancelar
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleUploadConfirm}
            loading={isSubmitting}
            disabled={!uploadModalFile || isSubmitting}
          >
            Guardar Imagen
          </Button>
        ]}
        destroyOnClose
        closable={!isSubmitting}
        maskClosable={false}
        width={500}
      >
        {uploadModalCategory && uploadModalType && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Sección de Imagen Actual */}
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Imagen Actual:</div>
              <div style={{ background: '#f5f5f5', border: '1px solid #e8e8e8', borderRadius: '6px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                {(() => {
                  const currentImgUrl = 
                    uploadModalType === "banner" ? uploadModalCategory.image_banner_url :
                    uploadModalType === "icon" ? uploadModalCategory.image_icon_url :
                    uploadModalCategory.image_icon_app_url;
                  
                  if (currentImgUrl) {
                    return (
                      <img 
                        src={currentImgUrl} 
                        alt="Actual" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '120px', 
                          objectFit: 'contain',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }} 
                      />
                    );
                  }
                  return <Text type="secondary" italic>Sin imagen actual</Text>;
                })()}
              </div>
            </div>

            {/* Selector de Nueva Imagen */}
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Seleccionar Nueva Imagen:</div>
              <Upload.Dragger
                beforeUpload={(file) => {
                  setUploadModalFile(file);
                  const previewUrl = URL.createObjectURL(file);
                  setUploadModalPreview(previewUrl);
                  return false; // Prevent auto-upload
                }}
                fileList={uploadModalFile ? [uploadModalFile] : []}
                onRemove={() => {
                  setUploadModalFile(null);
                  setUploadModalPreview(null);
                }}
                showUploadList={false}
                accept={
                  uploadModalType === "icon_app" ? "image/png" :
                  uploadModalType === "icon" ? "image/*,image/svg+xml" : "image/*"
                }
                disabled={isSubmitting}
              >
                <p className="ant-upload-drag-icon">
                  <PictureOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text" style={{ fontSize: '13px', padding: '0 10px' }}>
                  Haga clic o arrastre el archivo de imagen aquí para cargarlo
                </p>
                {uploadModalType === "icon_app" && (
                  <p className="ant-upload-hint" style={{ fontSize: '11px', color: '#fa8c16' }}>
                    Recomendado: Formato PNG
                  </p>
                )}
              </Upload.Dragger>
            </div>

            {/* Vista Previa de Nueva Selección */}
            {uploadModalPreview && (
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#52c41a' }}>Vista Previa de Selección:</div>
                <div style={{ background: '#f6ffed', border: '1px dashed #b7eb8f', borderRadius: '6px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
                  <img 
                    src={uploadModalPreview} 
                    alt="Vista Previa Nueva" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '120px', 
                      objectFit: 'contain',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }} 
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de configuración de canales configurables */}
      <Modal
        title="Configurar Canales Disponibles"
        visible={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        onOk={handleSaveConfig}
        confirmLoading={savingCustomizable}
        destroyOnClose
        okText="Guardar"
        cancelText="Cancelar"
        width={450}
      >
        <div style={{ marginBottom: 20 }}>
          <Text type="secondary">
            Selecciona qué canales podrán ser personalizados de forma individual (estado activo y orden de categorías) en esta pantalla. Los canales no seleccionados se ocultarán del selector y de la tabla.
          </Text>
        </div>
        {loadingCustomizable ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {tempCustomizableChannels.map(ch => (
              <div key={ch.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f9f9f9", borderRadius: "6px" }}>
                <Text strong style={{ fontSize: 14 }}>{ch.name.toUpperCase()}</Text>
                <Switch
                  checked={ch.isCustomizable}
                  onChange={(checked) => handleToggleChannelCustomizable(ch.id, checked)}
                />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Drawer de productos de la categoría */}
      <Drawer
        title={selectedCategoryForProducts ? `PRODUCTOS EN ${selectedCategoryForProducts.name.toUpperCase()}` : "PRODUCTOS EN LA CATEGORÍA"}
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedCategoryForProducts(null);
          setDrawerProducts([]);
        }}
        width={750}
        destroyOnClose
      >
        {drawerLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" tip="Cargando productos..." />
          </div>
        ) : (
          <Table
            columns={drawerColumns}
            dataSource={drawerProducts}
            rowKey="id"
            pagination={{
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"]
            }}
            bordered
            size="small"
          />
        )}
      </Drawer>
    </div>
  );
};

export default CategoriesUI;
